import { Component, computed, forwardRef, input, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorBase } from '../../../core/control-value-accessor-base';
import { AsyncPipe, NgClass } from '@angular/common';
import { TranslatePipe } from '@core/pipes';
import { SubscriptionStatus } from '@core/enums/subscription-status.enum';

const statusClassMap = new Map<SubscriptionStatus, string>([
  [SubscriptionStatus.PendingActivation, 'bg-orange-400 ring-orange-400'],
  [SubscriptionStatus.PendingDeactivation, 'bg-orange-400 ring-orange-400'],
  [SubscriptionStatus.Active, 'bg-success ring-success'],
  [SubscriptionStatus.Inactive, 'bg-primaryRed ring-primaryRed'],
]);

@Component({
  selector: 'sc-switch',
  imports: [NgClass, TranslatePipe, AsyncPipe],
  templateUrl: './switch.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SwitchComponent),
    },
  ],
})
export class SwitchComponent extends ControlValueAccessorBase<boolean> {
  ariaLabeledBy = input<string>(null);
  status = input<SubscriptionStatus | null>(null);
  private toggleIntent = signal<boolean | null>(null);
  disabledStatus = input<boolean>(false);

  toggle(): void {
    this.writeValue(!this.value);
    this.toggleIntent.set(this.value);
    this.onChange(this.value);
  }

  clearToggleIntent(): void {
    this.toggleIntent.set(null);
  }

  switchClasses = computed(() => {
    const intent = this.toggleIntent();
    const status = this.status();

    if (intent !== null) {
      return statusClassMap.get(intent ? SubscriptionStatus.Active : SubscriptionStatus.Inactive);
    }

    return statusClassMap.get(
      status ?? (this.value ? SubscriptionStatus.Active : SubscriptionStatus.Inactive)
    );
  });

  override writeValue(value: boolean): void {
    super.writeValue(value);
    this.toggleIntent.set(null);
  }

  public updateValue(newValue: boolean): void {
    super.writeValue(newValue);
    this.toggleIntent.set(null);
    this.onChange?.(newValue);
  }
}
